import { useDebounce } from "@/common/helpers/useDebounce";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	CustomTableColumnDefinition,
	generateColumnsDefinitionArray,
	TableColumnInfo,
} from "@/common/types";
import { TableSettingsCache } from "@/components/atoms/customTable";
import {
	SortDirection,
	TableColumnId,
	TableColumnSizingOptions,
	TableRowData,
	useTableColumnSizing_unstable,
	useTableFeatures,
	useTableSort,
} from "@fluentui/react-components";
import { useEffect, useMemo, useRef, useState } from "react";
import { useColumnManagement } from "../columnManagement";

export interface TableFeatures<T> {
	columns: CustomTableColumnDefinition<T>[];
	removeColumn: (index: number) => void;
	addDeletedColumn: () => void;
	columnSizing: any;
	headerSortProps: (columnId: TableColumnId) => {
		onClick: (e: React.MouseEvent) => void;
		sortDirection: SortDirection | undefined;
	};
	rows: TableRowData<T>[];
	resetToDefaultTableSettings: () => void;
	resetToVisibleDefaultColumns: () => void;
	localTableSettingsCache?: TableSettingsCache;
	addColumn: (tableColumnId: TableColumnId) => void;
}

export const useTableColumns = <T>(
	tableColumnInfo: TableColumnInfo[],
	items: T[],
	tableCacheKey: CacheKey,
	defaultSort: { columnId: string; direction: "ascending" | "descending" },
	excludedColumnIds: string[] = []
): TableFeatures<T> => {
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const cacheDataRef = useRef<TableSettingsCache>();

	const tableSettingsCache = (appStoreCache[tableCacheKey] ||
		{}) as TableSettingsCache;

	const columnsDefinition = useMemo(
		() => generateColumnsDefinitionArray<any>(tableColumnInfo),
		[tableColumnInfo]
	);

	const {
		columns,
		removeColumn,
		resetDefaultColumns,
		addDeletedColumn,
		resetVisibleDefaultColumns,
		addColumn,
	} = useColumnManagement<CustomTableColumnDefinition<any>>(
		columnsDefinition,
		tableSettingsCache,
		excludedColumnIds
	);

	const [columnSizingOptions, setColumnSizingOptions] =
		useState<TableColumnSizingOptions>({
			...columns.reduce((acc, column) => {
				acc[column.columnId] = {
					minWidth: column.minWidth,
					idealWidth:
						tableSettingsCache.columnWidths?.find(
							(x) => x.columnId == column.columnId
						)?.idealWidth ?? column.idealWidth,
				};
				return acc;
			}, {} as TableColumnSizingOptions),
		});

	const [sortState, setSortState] = useState<{
		sortDirection: "ascending" | "descending";
		sortColumn: TableColumnId | undefined;
	}>({
		sortDirection:
			tableSettingsCache?.sortSettings?.direction ?? defaultSort.direction,
		sortColumn:
			tableSettingsCache?.sortSettings?.columnId ?? defaultSort.columnId,
	});

	useEffect(() => {
		const currentColumnIds = columns.map(({ columnId }) => columnId);
		const allColumnIds = columnsDefinition.map((x) => x.columnId);
		// Excluded columns are absent because the tenant configuration has nothing to show in them, not
		// because the agent hid them. Persisting them as hidden would keep them gone after the tenant
		// gains its first tag (or enables skill routing).
		const missingColumnIds = allColumnIds.filter(
			(x) =>
				!currentColumnIds.includes(x) &&
				!excludedColumnIds.includes(x.toString())
		);

		let cacheModel = {
			sortSettings: {
				columnId: sortState.sortColumn,
				direction: sortState.sortDirection,
			},
			hiddenColumnIds: missingColumnIds,
			columnWidths: Object.keys(columnSizingOptions).map((key) => ({
				columnId: key,
				idealWidth: columnSizingOptions[key]?.idealWidth,
			})),
		} as TableSettingsCache;

		cacheDataRef.current = cacheModel;

		saveCacheModel();
	}, [
		columnSizingOptions,
		columns,
		sortState,
		tableColumnInfo,
		excludedColumnIds.join(","),
	]);

	const resetToVisibleDefaultColumns = () => {
		resetVisibleDefaultColumns();
	};

	useEffect(() => {
		return () => {
			appStoreActions.setCache(tableCacheKey, cacheDataRef.current);
		};
	}, []);

	const [saveCacheModel] = useDebounce(() => {
		appStoreActions.setCache(tableCacheKey, cacheDataRef.current);
	}, 500);

	const onColumnResize = (_: any, { columnId, width }: any) => {
		setColumnSizingOptions((state) => ({
			...state,
			[columnId]: {
				...state[columnId],
				idealWidth: width,
			},
		}));
	};

	const {
		getRows,
		sort: { getSortDirection, sort },
		columnSizing_unstable,
	} = useTableFeatures(
		{
			columns: columns,
			items: items ?? [],
		},
		[
			useTableColumnSizing_unstable({
				columnSizingOptions,
				onColumnResize,
				autoFitColumns: false,
			}),
			useTableSort({
				sortState: sortState,
			}),
		]
	);

	const onSortChange = (
		_: React.MouseEvent,
		columnId: TableColumnId | undefined
	) => {
		const sortDirection =
			sortState.sortDirection !== "ascending" ? "ascending" : "descending";

		setSortState({ sortDirection: sortDirection, sortColumn: columnId });
	};

	const headerSortProps = (columnId: TableColumnId) => ({
		onClick: (e: React.MouseEvent) => {
			onSortChange(e, columnId);
		},
		sortDirection: getSortDirection(columnId),
	});

	const rows = sort(getRows());

	const resetToDefaultTableSettings = () => {
		resetDefaultColumns();

		setColumnSizingOptions({
			...columnsDefinition.reduce((acc, column) => {
				acc[column.columnId] = {
					minWidth: column.minWidth,
					idealWidth: column.idealWidth,
				};
				return acc;
			}, {} as TableColumnSizingOptions),
		});

		setSortState({
			sortColumn: defaultSort.columnId,
			sortDirection: defaultSort.direction,
		});
	};

	return {
		columns,
		removeColumn,
		addDeletedColumn,
		columnSizing: columnSizing_unstable,
		headerSortProps,
		rows,
		resetToDefaultTableSettings,
		resetToVisibleDefaultColumns,
		localTableSettingsCache: cacheDataRef.current,
		addColumn,
	};
};
